/**
 * VoiceSelector Component
 * Grid of available voices with preview and selection
 */

import React from 'react';
import { FiCheck, FiLoader, FiUser } from 'react-icons/fi';
import type { Voice, VoiceProvider } from '../../types/voice.types';

interface VoiceSelectorProps {
  voices: Voice[];
  selectedVoice: Voice | null;
  onSelect: (voice: Voice) => void;
  loading?: boolean;
  provider: VoiceProvider;
  className?: string;
}

export const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  voices,
  selectedVoice,
  onSelect,
  loading = false,
  provider,
  className = ''
}) => {
  if (loading) {
    return (
      <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 ${className}`}>
        <div className="flex items-center justify-center py-12">
          <FiLoader className="animate-spin text-blue-400" size={32} />
          <span className="ml-3 text-gray-400">Loading voices...</span>
        </div>
      </div>
    );
  }

  if (voices.length === 0) {
    return (
      <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 ${className}`}>
        <div className="text-center py-12">
          <FiUser className="mx-auto text-gray-500 mb-3" size={48} />
          <p className="text-gray-400">No voices available for {provider}</p>
          <p className="text-sm text-gray-500 mt-2">
            Check your API credentials or try a different provider
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 ${className}`}>
      <div className="p-4 border-b border-slate-700">
        <h3 className="text-lg font-semibold text-white">Available Voices</h3>
        <p className="text-sm text-gray-400 mt-1">{voices.length} voices available</p>
      </div>

      <div className="p-4 max-h-[500px] overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {voices.map((voice) => {
            const isSelected = selectedVoice?.id === voice.id;

            return (
              <button
                key={voice.id}
                onClick={() => onSelect(voice)}
                className={`
                  relative p-4 rounded-lg border transition-all text-left
                  ${isSelected
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-700 bg-slate-900/50 hover:border-slate-600 hover:bg-slate-800/50'
                  }
                `}
              >
                {/* Selection Indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <FiCheck size={14} className="text-white" />
                  </div>
                )}

                {/* Voice Name */}
                <div className="font-medium text-white mb-1">{voice.name}</div>

                {/* Voice Metadata */}
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="capitalize">{voice.language}</span>
                  {voice.gender && (
                    <>
                      <span>•</span>
                      <span className="capitalize">{voice.gender}</span>
                    </>
                  )}
                </div>

                {/* Voice Description */}
                {voice.description && (
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                    {voice.description}
                  </p>
                )}

                {/* Preview Badge */}
                {voice.previewUrl && (
                  <div className="mt-2">
                    <span className="inline-block px-2 py-1 text-xs bg-slate-700 text-gray-300 rounded">
                      Preview Available
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VoiceSelector;
